import {
  AddCircleOutlined,
  BusinessOutlined,
  CalendarTodayOutlined,
  DescriptionOutlined,
  InventoryOutlined,
  RefreshOutlined,
  SendOutlined,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import { useAppController } from 'customHooks/useAppController'
import { useDialogManager } from 'features/DialogManager/useDialogManager'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  MoneyDto,
  RequestForQuotationDto,
  RequestForQuotationState,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { makeStyles } from 'tss-react/mui'
import { useShowException } from 'utils/useShowException'
import { ProjectRFQController } from '../ProjectRFQController'

export type ManageRFQDialogProps = {
  isOpen: boolean
  onClose: () => void
}

const useStyles = makeStyles({ name: 'ManageRFQDialog' })((theme) => ({
  rfqCard: {
    padding: theme.spacing(2),
    borderRadius: `calc(${theme.shape.borderRadius}px * 2)`,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'border-color 0.2s, box-shadow 0.2s',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 1px ${theme.palette.primary.main}20`,
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  cardTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
  },
  infoRow: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap' as const,
    alignItems: 'center',
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  activitiesRow: {
    display: 'flex',
    gap: theme.spacing(0.5),
    flexWrap: 'wrap' as const,
    alignItems: 'center',
  },
  quotationsSummary: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap' as const,
    alignItems: 'center',
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
}))

function getStatusColor(
  status: string | undefined,
): 'success' | 'error' | 'warning' | 'info' | 'default' {
  switch (status) {
    case RequestForQuotationState.New:
    case RequestForQuotationState.DocumentCreated:
      return 'info'
    case RequestForQuotationState.RequestForQuotationSent:
    case RequestForQuotationState.Parsed:
      return 'warning'
    case RequestForQuotationState.ProjectCreated:
    case RequestForQuotationState.Accepted:
    case RequestForQuotationState.QuotationResponseSent:
      return 'success'
    case RequestForQuotationState.RequestForQuotationReceivedError:
    case RequestForQuotationState.ParsedError:
    case RequestForQuotationState.ProjectCreatedError:
    case RequestForQuotationState.QuotationResponseSentError:
      return 'error'
    default:
      return 'default'
  }
}

function isNewStatus(status: string | undefined): boolean {
  return status === RequestForQuotationState.New
}

function formatMoney(money: MoneyDto | undefined): string {
  if (!money?.value && money?.value !== 0) return '-'
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: money.currency || 'EUR',
    }).format(money.value)
  } catch {
    return `${money.value} ${money.currency || ''}`
  }
}

function getUniqueWorkingSteps(
  bomItemsAndActivities:
    | { activities?: { workingStepType?: WorkingStepType }[] | undefined }[]
    | undefined,
): WorkingStepType[] {
  if (!bomItemsAndActivities) return []
  const steps = new Set<WorkingStepType>()
  bomItemsAndActivities.forEach((item) =>
    item.activities?.forEach((a) => {
      if (a.workingStepType) steps.add(a.workingStepType)
    }),
  )
  return Array.from(steps)
}

const RFQCard = ({
  rfq,
  loading,
  onSend,
}: {
  rfq: RequestForQuotationDto
  loading: Record<string, boolean>
  onSend: (rfq: RequestForQuotationDto) => void
}) => {
  const { classes } = useStyles()
  const getLocalizedWorkingStepName = useLocalizedWorkingStepLabel()

  const rfqData = rfq
  const quotations = []
  const sendLoading = loading[`sendRequestForQuotation-${rfqData.id}`] || false

  const workingSteps = useMemo(
    () => getUniqueWorkingSteps(rfqData.bomItemsAndActivities),
    [rfqData.bomItemsAndActivities],
  )

  const bomItemCount = rfqData.bomItemsAndActivities?.length ?? 0

  return (
    <Paper className={classes.rfqCard} elevation={0}>
      {/* Header: title + status + action */}
      <div className={classes.cardHeader}>
        <div className={classes.cardTitle}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
            {rfqData.fileName || `RFQ ${rfqData.id.substring(0, 8)}…`}
          </Typography>
          <Chip
            label={rfqData.status || 'Unknown'}
            size="small"
            color={getStatusColor(rfqData.status)}
          />
        </div>

        {isNewStatus(rfqData.status) && (
          <Tooltip title="Send to supplier">
            <span>
              <Button
                variant="contained"
                size="small"
                color="primary"
                startIcon={
                  sendLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SendOutlined fontSize="small" />
                  )
                }
                disabled={sendLoading}
                onClick={() => onSend(rfqData)}
              >
                Send
              </Button>
            </span>
          </Tooltip>
        )}
      </div>

      {/* Body: details */}
      <div className={classes.cardBody}>
        {/* Info row: supplier, buyer, dates, bom items */}
        <div className={classes.infoRow}>
          {rfqData.forwardId && (
            <Tooltip title="Supplier">
              <span className={classes.infoItem}>
                <BusinessOutlined sx={{ fontSize: 16 }} />
                <Typography variant="body2">{rfqData.forwardId}</Typography>
              </span>
            </Tooltip>
          )}

          {rfqData.buyerName && (
            <Tooltip title="Buyer">
              <span className={classes.infoItem}>
                <DescriptionOutlined sx={{ fontSize: 16 }} />
                <Typography variant="body2">{rfqData.buyerName}</Typography>
              </span>
            </Tooltip>
          )}

          <Tooltip title="Created">
            <span className={classes.infoItem}>
              <CalendarTodayOutlined sx={{ fontSize: 16 }} />
              <Typography variant="body2">
                {moment(rfqData.dateCreated).format('L LT')}
              </Typography>
            </span>
          </Tooltip>

          {bomItemCount > 0 && (
            <Tooltip title="BOM items">
              <span className={classes.infoItem}>
                <InventoryOutlined sx={{ fontSize: 16 }} />
                <Typography variant="body2">
                  {bomItemCount} item{bomItemCount !== 1 ? 's' : ''}
                </Typography>
              </span>
            </Tooltip>
          )}
        </div>

        {/* Activities */}
        {workingSteps.length > 0 && (
          <div className={classes.activitiesRow}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mr: 0.5 }}
            >
              Activities:
            </Typography>
            {workingSteps.map((ws) => (
              <Chip
                key={ws}
                label={getLocalizedWorkingStepName(ws)}
                size="small"
                variant="outlined"
              />
            ))}
          </div>
        )}

        {/* Quotations summary */}
        {quotations && quotations.length > 0 && (
          <>
            <Divider />
            <div className={classes.quotationsSummary}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {quotations.length} Quotation
                {quotations.length !== 1 ? 's' : ''}
              </Typography>
              {quotations.map((q) => (
                <Chip
                  key={q.id}
                  size="small"
                  variant="outlined"
                  label={`${q.sellingPartyName || q.id.substring(0, 8)} — ${formatMoney(q.taxExclusiveAmount)}`}
                  color={
                    q.state === 'QuotationResponseAcceptedByBuyer'
                      ? 'success'
                      : q.state === 'QuotationResponseRejectedByBuyer' ||
                          q.state === 'Cancelled'
                        ? 'error'
                        : 'default'
                  }
                />
              ))}
            </div>
          </>
        )}
      </div>
    </Paper>
  )
}

export const ManageRFQDialog = (props: ManageRFQDialogProps) => {
  const { classes } = useStyles()
  const { openDialog } = useDialogManager()
  const { controller, loading } = useAppController(
    () => new ProjectRFQController(),
  )

  const [rfqs, setRFQs] = useState<RequestForQuotationDto[]>([])

  const ShowException = useShowException('Request for Quotations')
  const isLoading = loading['getRFQs']

  const fetchData = useCallback(async () => {
    try {
      const data = await controller.getRFQs()
      setRFQs(data.details ?? [])
    } catch (err) {
      ShowException(err)
    }
  }, [controller, ShowException])

  useEffect(() => {
    if (props.isOpen) {
      fetchData()
    }
  }, [props.isOpen, fetchData])

  const handleSendRFQ = useCallback(
    (rfq: RequestForQuotationDto) => {
      controller
        .sendRequestForQuotation({
          requestForQuotationId: rfq.id,
          forwardId: rfq.forwardId,
        })
        .then(() => fetchData())
        .catch((err) => ShowException(err))
    },
    [controller, fetchData, ShowException],
  )

  const handleOpenDraftDialog = useCallback(() => {
    openDialog('DraftRFQDialog')
  }, [openDialog])

  return (
    <Dialog
      open={props.isOpen}
      onClose={props.onClose}
      fullWidth
      maxWidth="md"
      slotProps={{
        paper: { sx: { minHeight: '50vh', maxHeight: '80vh' } },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="h6" component="span">
            Request for Quotations
          </Typography>
          <Chip
            label={rfqs.length}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchData} disabled={isLoading} size="small">
              {isLoading ? (
                <CircularProgress size={18} />
              ) : (
                <RefreshOutlined fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddCircleOutlined />}
            onClick={handleOpenDraftDialog}
          >
            New RFQ
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}
      >
        {rfqs.length === 0 && !isLoading && (
          <div className={classes.emptyState}>
            <Typography variant="body1">No RFQs found</Typography>
            <Typography variant="body2">
              Create a new RFQ to get started
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlined />}
              onClick={handleOpenDraftDialog}
            >
              Create RFQ
            </Button>
          </div>
        )}

        {rfqs.map((rfq) => (
          <RFQCard
            key={rfq.id}
            rfq={rfq}
            loading={loading}
            onSend={handleSendRFQ}
          />
        ))}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button variant="outlined" onClick={props.onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export const OpenManageRFQDialogButton = () => {
  const { openDialog } = useDialogManager()

  return (
    <Button onClick={() => openDialog('ManageRFQDialog')}>Manage RFQs</Button>
  )
}
